import { useCallback, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { EonClusterDetailsGQL } from "../graphql";
import { useAppDispatch } from "../components/hooks";
import {
  ClientClusterMetric,
  setClientClusterMetrics,
} from "../store/slices/appState";

const PING_TIMEOUT_MS = 5000;
const PING_INTERVAL_MS = 60000;
const PING_ATTEMPTS = 4;

export interface PingClustersOptions {
  attempts?: number;
  timeoutMs?: number;
}

const pingDomain = async (
  domain: string,
  timeoutMs: number,
): Promise<number | null> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const url = domain.startsWith("http") ? domain : `https://${domain}`;
  const start = performance.now();

  try {
    await fetch(url, {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    });
    return Math.round(performance.now() - start);
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

const pingCluster = async (
  name: string,
  domain: string,
  attempts: number,
  timeoutMs: number,
): Promise<ClientClusterMetric> => {
  const metrics: { ping: number }[] = [];
  for (let i = 0; i < attempts; i++) {
    const ping = await pingDomain(domain, timeoutMs);
    if (ping !== null) metrics.push({ ping });
  }
  return { name, reachable: metrics.length > 0, metrics };
};

/**
 * One-shot ping of all EON clusters. Clusters are pinged in parallel; each
 * cluster runs `attempts` sequential pings. Reusable outside the polling hook
 * (e.g. a quick pre-launch ping in the Android overlay, which has no MainLayout
 * to run usePingEonCluster).
 */
export const pingEonClusters = async (
  clusters: { name: string; domain: string }[],
  {
    attempts = PING_ATTEMPTS,
    timeoutMs = PING_TIMEOUT_MS,
  }: PingClustersOptions = {},
): Promise<ClientClusterMetric[]> =>
  Promise.all(
    clusters.map(({ name, domain }) =>
      pingCluster(name, domain, attempts, timeoutMs),
    ),
  );

export const usePingEonCluster = (skip?: boolean) => {
  const dispatch = useAppDispatch();
  const { data, refetch } = useQuery(EonClusterDetailsGQL, { skip });
  const clusters = data?.eonClusterDetails;

  const fetchEonCluster = useCallback(async () => {
    const data = await refetch({ fetchPolicy: "network-only" });
    const clusters = data?.data?.eonClusterDetails;
    const metrics = await pingEonClusters(
      clusters as { name: string; domain: string }[],
    );
    dispatch(setClientClusterMetrics(metrics));
    return metrics;
  }, [dispatch, refetch]);

  useEffect(() => {
    if (!clusters?.length || skip) return;
    let cancelled = false;

    const run = async () => {
      const metrics = await pingEonClusters(
        clusters as { name: string; domain: string }[],
      );

      if (!cancelled) dispatch(setClientClusterMetrics(metrics));
    };

    run();
    const intervalId = setInterval(run, PING_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [clusters, dispatch, skip]);

  return { fetchEonCluster };
};
